import React, { FC } from 'react';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import { StorageData, useStorage } from '~Components/Storage';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import Toggle from '~ComponentsImproved/Toggle';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useI18n, useLogger, useSelector } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import { gql, useQuery } from '@apollo/client';
import { GetTicketTemplate, GetTicketTemplateVariables } from './__queries__';

const TICKET_TEMPLATE_QUERY = gql`
  query GetTicketTemplate($venueId: String!) {
    operativeViewer {
      ticketTemplates(venueId: $venueId) {
        id
        stationIds
      }
    }
  }
`;

const KitchenTicketsSettings: FC = () => {
  const { i18n } = useI18n();
  const venueId = useSelector((state) => state.venueId);
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2');

  const ticketTemplateQuery = useQuery<GetTicketTemplate, GetTicketTemplateVariables>(
    TICKET_TEMPLATE_QUERY,
    {
      variables: { venueId: venueId! },
      skip: !venueId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const templates = ticketTemplateQuery.data?.operativeViewer?.ticketTemplates || [];
  // Check if there is a template that is for all stations (aka default template). If there is a template, we hide settings that are overridden by the templates to avoid confusion.
  const hasDefaultTemplate = templates.some((template) => template.stationIds.length === 0);

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Bong Settings)', { fields: data });
    commitToStorage(data);
  }

  const updateOrderStatus = (status: ORDER_STATUS | null) => {
    logger.info('Updating Settings (Bong Settings) - Order status', { fields: { status } });
    updateStorage({ KDSBongOnOrderStatus: status });
  };

  const togglesConfig: Array<{
    label: string;
    subLabel?: string;
    key: string;
    storage: any;
    // Will reverse the value of the storage
    inverted?: boolean;
    hide?: boolean;
  }> =
    /**
     * If bong printing is disabled, hide all the other settings since
     * they make no sense.
     */
    storage.enableBongPrints
      ? [
          {
            label: i18n('Settings.PrintKitchenBongSlips'),
            subLabel: i18n('Settings.PrintKitchenBongSlips.Text'),
            key: 'enableBongPrints',
            storage: storage.enableBongPrints,
          },
          {
            label: i18n('Settings.Bong.DisableCapitalLetters'),
            subLabel: i18n('Settings.Bong.DisableCapitalLetters.Text'),
            key: 'BongDisableCapitalLetters',
            storage: storage.BongDisableCapitalLetters,
            hide: hasDefaultTemplate, // If there is a template, the capital letters setting is overridden
          },
          {
            label: i18n('Settings.Bong.HideAllExceptDesiredTime'),
            subLabel: i18n('Settings.Bong.HideAllExceptDesiredTime.Text'),
            key: 'BongHideAllExceptDesiredTime',
            storage: storage.BongHideAllExceptDesiredTime,
            hide: hasDefaultTemplate, // If there is a template, the hide all except desired time setting is overridden
          },
          {
            label: i18n('Settings.BongPrintByStation'),
            subLabel: i18n('Settings.BongPrintByStation.Text'),
            key: 'bongPrintByStation',
            storage: storage.bongPrintByStation,
          },
          {
            label: i18n('Settings.GroupKitchenBongsStation'),
            subLabel: i18n('Settings.GroupKitchenBongsStation.Text'),
            key: 'bongGroupByStation',
            storage: storage.bongGroupByStation,
          },
          {
            label: i18n('Settings.GroupKitchenBongsCategory'),
            subLabel: i18n('Settings.GroupKitchenBongsCategory.Text'),
            key: 'bongGroupByCategory',
            storage: storage.bongGroupByCategory,
          },
          {
            label: i18n('Settings.Bong.ShowFloorplan'),
            subLabel: i18n('Settings.Bong.ShowFloorplan.Text'),
            key: 'bongShowFloorplan',
            storage: storage.bongShowFloorplan,
            hide: hasDefaultTemplate, // If there is a template, the show floorplan setting is overridden
          },
          {
            label: i18n('Settings.Bong.ShowPrintId'),
            subLabel: i18n('Settings.Bong.ShowPrintId.Text'),
            key: 'BongShowPrintId',
            storage: storage.BongShowPrintId,
            hide: hasDefaultTemplate, // If there is a template, the show print id setting is overridden
          },
          {
            label: i18n('Settings.Printer.EpsonRetry'),
            subLabel: i18n('Settings.Printer.EpsonRetry.Text'),
            key: 'enableEpsonRetry',
            storage: storage.enableEpsonRetry,
          },
          {
            label: i18n('Settings.Bong.ShowPrices'),
            subLabel: i18n('Settings.Bong.ShowPrices.Text'),
            key: 'bongShowPrices',
            storage: storage.bongShowPrices,
            hide: hasDefaultTemplate, // If there is a template, the show prices setting is overridden
          },
          {
            label: i18n('Settings.PrintVoidedItems'),
            subLabel: i18n('Settings.PrintVoidedItems.Desc'),
            key: 'printVoidedItems',
            storage: storage.printVoidedItems,
          },
          {
            label: i18n('Settings.AskToPrintBong'),
            subLabel: i18n('Settings.AskToPrintBong.Desc'),
            key: 'askToPrintBong',
            storage: storage.askToPrintBong,
          },
          {
            label: i18n('Settings.Bong.SortItemsAlphabetically'),
            subLabel: i18n('Settings.Bong.SortItemsAlphabetically.Text'),
            key: 'bongSortItemsAlphabetically',
            storage: storage.bongSortItemsAlphabetically,
          },
        ]
      : [
          {
            label: i18n('Settings.PrintKitchenBongSlips'),
            subLabel: i18n('Settings.PrintKitchenBongSlips.Text'),
            key: 'enableBongPrints',
            storage: storage.enableBongPrints,
          },
          {
            label: i18n('Settings.PrintVoidedItems'),
            subLabel: i18n('Settings.PrintVoidedItems.Desc'),
            key: 'printVoidedItems',
            storage: storage.printVoidedItems,
          },
        ];

  const radioButtonsConfig: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: Array<{
      id: string;
      label: string;
      value: ORDER_STATUS | null;
    }>;
  }[] = [
    {
      containerLabel: i18n('Settings.KDS.PhysicalPrint'),
      containerDesc: i18n('Settings.KDS.PhysicalPrint.Desc'),
      name: 'status',
      buttons: [
        {
          id: 'NONE',
          label: i18n('Settings.KDS.NoPrint'),
          value: null,
        },
        {
          id: ORDER_STATUS.ACCEPTED,
          label: i18n('Settings.KDS.Accepted'),
          value: ORDER_STATUS.ACCEPTED,
        },
        {
          id: ORDER_STATUS.FINALIZING,
          label: i18n('Settings.KDS.Finalizing'),
          value: ORDER_STATUS.FINALIZING,
        },
        {
          id: ORDER_STATUS.READY,
          label: i18n('Settings.KDS.Ready'),
          value: ORDER_STATUS.READY,
        },
      ],
    },
  ];

  if (storageLoading) {
    return null;
  }

  return (
    <Box padding="large">
      <Heading level={4} bottom={8} color="var(--colorWhite)">
        {i18n('Settings.Title')}
      </Heading>
      <SettingsBlock>
        {togglesConfig.map(
          (toggle) =>
            !toggle.hide && (
              <Toggle
                key={toggle.key}
                settingKey={toggle.key}
                label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
                description={toggle.subLabel}
                initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
                onToggle={() => {
                  const newValue = !toggle.storage;
                  // When enabling Epson retry, automatically enable BongShowPrintId to help identify duplicate prints
                  if (toggle.key === 'enableEpsonRetry' && newValue) {
                    updateStorage({ [toggle.key]: newValue, BongShowPrintId: true });
                  } else {
                    updateStorage({ [toggle.key]: newValue });
                  }
                }}
                fullWidth
                darkBackground
                lightText
              />
            ),
        )}
        {radioButtonsConfig.map((config) => (
          <RadioButtonSelect
            key={config.name}
            settingKey="KDSBongOnOrderStatus"
            data={config}
            selected={storage.KDSBongOnOrderStatus ?? 'NONE'}
            handleChange={(value) => updateOrderStatus(value)}
          />
        ))}
      </SettingsBlock>

      <Box marginTop="large">
        <Heading level={4} bottom={8} color="var(--colorWhite)">
          Experimental features
        </Heading>
        <SettingsBlock>
          <Toggle
            label={`${i18n('Settings.UseNewPrinterFlow')} ${
              storage.devMode ? '[useNewPrinterFlow]' : ''
            } `}
            settingKey="useNewPrinterFlow"
            description={i18n('Settings.UseNewPrinterFlow.Desc')}
            initialValue={storage.useNewPrinterFlow}
            onToggle={() => {
              const newValue = !storage.useNewPrinterFlow;

              if (newValue === false) {
                updateStorage({ useNewPrinterFlow: newValue, useImagePrinting: false });
              } else {
                updateStorage({ useNewPrinterFlow: newValue });
              }
            }}
            fullWidth
            darkBackground
            lightText
          />

          {storage.useNewPrinterFlow && (
            <Toggle
              label={`${i18n('Settings.UseImagePrinting')} ${
                storage.devMode ? '[useImagePrinting]' : ''
              } `}
              settingKey="useImagePrinting"
              description={i18n('Settings.UseImagePrinting.Desc')}
              initialValue={storage.useImagePrinting}
              onToggle={() => updateStorage({ useImagePrinting: !storage.useImagePrinting })}
              fullWidth
              darkBackground
              lightText
            />
          )}
        </SettingsBlock>
      </Box>
    </Box>
  );
};

export default KitchenTicketsSettings;
